import { separateMoneyNumbers, toFarsiNumber } from "@utils/digits";
import Link from "next/link";
import Graduated from "@svg/new-design/graduated";
import ArrowLeft from "@svg/new-design/arrow-left";
import ImageComponent from "@shared/components/image";
import ClockIcon from "@svg/new-design/clock";
import Label from "@shared/components/label";
import {
  ICON_COLOR,
  ICON_SIZE,
  ICON_VARIANT,
  IconButton,
} from "@shared/components/icon-button";
import BookmarkBold from "@svg/new-design/bookmark-bold";
import Bookmark from "@svg/new-design/bookmark";
import { useEffect, useState, MouseEvent } from "react";
import { modalActions } from "@context/modals";
import { MODAL_TYPES } from "types/modals";
import { useCardAction } from "@shared/components/card/useCartAction";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import { resolveProductImageSources } from "utils/product-image";
import { FAVORITE_TYPE } from "enums/favorite-type";
import {
  LABEL_COLOR,
  LABEL_SIZE,
  LABEL_STYLE,
  LABEL_TYPE,
} from "@shared/components/label/label";
import { Button } from "@shared/components";
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_VARIANT,
} from "@shared/components/button";

interface CollectionsProps {
  svg_image: string;
  png_image: string;
  student: number;
  duration: string | number;
  product: number;
  slug: string;
  title: string;
  isFavorite?: boolean;
  id?: number;
}

const SingleCollectionCard: React.FC<CollectionsProps> = ({
  svg_image,
  png_image,
  student,
  product,
  slug,
  title,
  duration,
  isFavorite,
  id,
}) => {
  const { addFavoriteCourse } = useCardAction();
  const [isLoading, setIsLoading] = useState(false);
  const [favorite, setIsFavorite] = useState(isFavorite || false);
  const formattedNumber = (title: number) =>
    title >= 1_000_000
      ? separateMoneyNumbers(toFarsiNumber(999_999)) + "+"
      : separateMoneyNumbers(toFarsiNumber(title));
  const isMobile = useIsMobile(905);

  const imageSources = resolveProductImageSources({ svg_image, png_image });
  useEffect(() => {
    setIsFavorite(isFavorite || false);
  }, [isFavorite]);

  const handleFavorite = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (favorite) {
      modalActions.addModal(MODAL_TYPES.REMOVE_CONFIRMATION, {
        data: {
          title: "حذف آموزش از علاقه‌مندی‌های من",
          description: `${title} از فهرست علاقه‌مندی‌های من حذف شود؟`,
          onSubmit: submitFavorite,
        },
        config: {
          hideCloseIcon: true,
          modalClassName: "rounded-t-24 rounded-b-16",
        },
      });
    } else {
      submitFavorite();
    }
  };

  const submitFavorite = async () => {
    modalActions.clearModals();
    setIsLoading(true);
    const res = await addFavoriteCourse?.(
      favorite,
      id ?? 0,
      FAVORITE_TYPE.HOW_TO_LEARN
    );

    setIsLoading(false);
    setIsFavorite(res);
  };

  return (
    <div className="w-full pb-2">
      <Link prefetch={false} href={`/how-to-learn/${slug}`}>
        <div className="grid w-full">
          <div
            aria-hidden
            className="col-start-1 -translate-y-1.5 translate-x-1 row-start-1 rounded-20 w-full pt-[3px]  bg-fds-brand-light-hover"
          />
          <div
            aria-hidden
            className="col-start-1  -translate-y-[3px] row-start-1 rounded-16 w-full pt-[3px]   bg-fds-brand-light"
          />
          <div className="col-start-1 z-10 row-start-1 rounded-12 fds-md:p-4 p-2 items-start mr-1  bg-fds-primary rounded-14   border border-fds-cart hover:shadow-brand-md  hover:border-fds-divider  transition-all duration-150 ease-in">
            <div className="  flex gap-x-2 fds-md:gap-x-4">
              <div className="relative rounded-4 overflow-hidden grow shrink-0 w-[112px] lg:w-[172px] aspect-video">
                <IconButton
                  variant={ICON_VARIANT.DEFAULT}
                  color={ICON_COLOR.DEFAULT}
                  onClick={handleFavorite}
                  icon={favorite ? <BookmarkBold /> : <Bookmark />}
                  size={isMobile ? ICON_SIZE.SMALL : ICON_SIZE.MEDIUM}
                  disabled={isLoading}
                  aria-label={
                    favorite ? "حذف از علاقمندی‌ها" : "افزودن به علاقمندی"
                  }
                  message={
                    favorite ? "حذف از علاقمندی‌ها" : "افزودن به علاقمندی"
                  }
                  iconClassName={favorite ? "!text-fds-icon-brand" : ""}
                  className="absolute right-0 top-0 z-[5]"
                />

                <ImageComponent
                  alt={title}
                  src={imageSources.src}
                  fallbackSrcs={imageSources.fallbackSrcs}
                  fill
                  loading="eager"
                />
              </div>
              <div className="w-full flex flex-col">
                <h2 className="md:title-lg title-sm text-fds-primary h-10 md:h-fds-52 line-clamp-2">
                  {title}
                </h2>
                <div className="flex items-center gap-x-3 mt-1 fds-md:mt-2">
                  <div className="flex w-fit  justify-start  items-center gap-1 fds-md:gap-1.5">
                    <ClockIcon className="md:h-fds-18 text-fds-icon-tertiary md:w-fds-18 h-4 w-4" />
                    <p className="md:body-md w-fit text-nowrap body-xxsm text-fds-secondary ">
                      {`${formattedNumber(Number(duration)) || ""}  ساعت`}
                    </p>
                  </div>
                  <div className="h-4     md:block hidden bg-fds-disable w-px   " />
                  <div className="flex  justify-start w-full items-center gap-1 fds-md:gap-1.5">
                    <Graduated className="md:h-fds-18 text-fds-icon-tertiary md:w-fds-18  h-4 w-4" />
                    <p className="md:body-md w-max body-xxsm text-fds-secondary ">
                      {`${formattedNumber(student)}  دانشجو`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t flex items-center justify-between border-fds-divider mt-3.5 pt-3.5 ">
              <Label
                color={LABEL_COLOR.DEFAULT}
                style={LABEL_STYLE.LIGHT}
                size={LABEL_SIZE.MEDIUM}
                type={LABEL_TYPE.ROUND}
                className="px-2 lg:body-md body-sm"
              >{`${formattedNumber(product)} آموزش `}</Label>
              <Button
                color={BUTTON_COLOR.PRIMARY}
                variant={BUTTON_VARIANT.TEXT_ACTION}
                size={BUTTON_SIZE.SMALL}
                endIcon={<ArrowLeft className=" h-4 w-4" />}
                className="self-end md:hidden flex  "
              >
                دیدن آموزش‌ها
              </Button>
              <Button
                color={BUTTON_COLOR.PRIMARY}
                variant={BUTTON_VARIANT.TEXT_ACTION}
                size={BUTTON_SIZE.MEDIUM}
                endIcon={<ArrowLeft className=" h-4 w-4" />}
                className="self-end hidden md:flex  "
              >
                دیدن آموزش‌ها{" "}
              </Button>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default SingleCollectionCard;
